import { zodResolver } from '@hookform/resolvers/zod';
import type { MeterInput } from '@vy/types';
import { AGGREGATION_OPERATORS } from '@vy/types';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useVyNavigate } from '../../../../../../hooks/use-vy-navigate';
import type { CreateProductDialogFormInput } from '../create-product-dialog-types/create-product-dialog-form-input-schema';
import type { NewCreateProductDialogForm } from '../create-product-dialog-types/new-create-product-dialog-form-schema';
import { NewCreateProductDialogFormSchema } from '../create-product-dialog-types/new-create-product-dialog-form-schema';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { CreateProductDialogContent } from './CreateProductDialogContent';
import { Box } from '@mui/material';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';

type CreateProductDialogProps = {
  openCreateProductDialog: boolean;
  onClose: () => void;
  onProductCreated?: (productName: string) => void;
  navigateBack?: () => void;
};

const CreateProductDialog: React.FC<CreateProductDialogProps> = ({
  openCreateProductDialog,
  onClose,
  onProductCreated,
  navigateBack
}) => {
  const navigate = useVyNavigate();

  const {
    control,
    handleSubmit: formHandleSubmit,
    register,
    formState: { errors, isValid },
  } = useForm<CreateProductDialogFormInput, unknown, NewCreateProductDialogForm>({
    resolver: zodResolver(NewCreateProductDialogFormSchema),
  });

  const handleSubmit = formHandleSubmit((data) => {
    if (onProductCreated) {
      onProductCreated(data.name);
    }

    navigate('/products/create', {
      replace: true,
      state: {
        ...data,
        aggregationMethod: {
          operator: AGGREGATION_OPERATORS.Count,
        },
      } satisfies MeterInput,
    });

    handleDialogClose();
  });

  const nameRegister = register('name');
  const descriptionRegister = register('description');

  const handleDialogClose = () => {
    onClose();
  };

  return (
    <VyDialog
      open={openCreateProductDialog}
      onClose={handleDialogClose}
      label="Metered product"
      ctaText="Go to product builder"
      ctaDisabled={!isValid}
      cancelButtonText="Cancel"
      disableDividers
      onSubmit={handleSubmit}
      maxWidth="md"
      navigateBack={navigateBack ?? handleDialogClose}
      cancelButtonProps={{
        sx: {
          color: 'rgba(13, 27, 39, 0.87)',
        }
      }}
      ctaButtonProps={{
        sx: {
          color: '#FFF',
          backgroundColor: '#0D0D48',
          "&:hover": {
            backgroundColor: '#0D0D48',
          }
        }
      }}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
        <CreateProductDialogContent
          control={control}
          errors={errors}
          nameRegister={nameRegister}
          descriptionRegister={descriptionRegister}
        />
      </Box>
    </VyDialog >
  );
};

const CreateProductDialogWithSuspense = withSuspenseBoundary(CreateProductDialog);

export { CreateProductDialogWithSuspense as CreateProductDialog };